import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useEmailAuthStore } from '../stores/auth_email';
import { useLoadingStore } from '../stores/useLoadingStore'
import { useCompanyStore } from '../stores/company'
import { usePermissions } from '../composables/usePermissions'
import { ensureViewerConfigLoaded } from '../composables/useViewerConfig'
import { ensureSessionCustomRoleLoaded } from '../composables/useSessionCustomRole'
import { modulesForPath, navigationItems } from '../constants/navigation'
import OnboardingLayout from '../layouts/OnboardingLayout.vue';
import DashboardLayout from '../layouts/DashboardLayout.vue';
import PublicLayout from '../layouts/PublicLayout.vue';
import { docgenRoutes } from '@/modules/docgen/routes';
import { assistantRoutes, assistantStandaloneRoutes } from '@/modules/assistant/routes';
/* import { planModules } from '@/constants/planModules';
import { useSubscription } from '@/composables/useSubscription';
import { getCurrentInstance } from 'vue';
 */
// Función auxiliar para obtener planName de manera segura

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [

    {
      path: '/',
      name: 'home', 
      meta: { requiresAuth: false },
      component: () => import('../pages/LoginPage.vue'),
    },
    {
      path: '/CEO',
      name: 'CEO', 
      meta: { requiresAuth: false },
      component: () => import('../pages/public/CEO.vue'),
    },
    {
      path: '/ProjectManager',
      name: 'ProjectManager', 
      meta: { requiresAuth: false },
      component: () => import('../pages/public/ProjectManager.vue'),
    },
    {
      path: '/login',
      name: 'login', 
      meta: { requiresAuth: false },
      component: () => import('../pages/LoginPage.vue'),
    },
    {
      path: '/signup',
      name: 'signup',
      meta: { requiresAuth: false },
      component: () => import('../pages/SignupPage.vue'),
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      meta: { requiresAuth: true },
      component: () => import('../pages/VerifyEmailPage.vue'),
    },

    // OAuth callback para email (el backend redirige a /auth/callback)
    {
      path: '/auth/callback',
      name: 'auth-callback',
      meta: { requiresAuth: false },
      component: () => import('../pages/email/CallbackView.vue'),
    },

    // Ruta pública para ver cotizaciones compartidas
    {
      path: '/quote/preview/:id',
      name: 'quote-preview',
      meta: { requiresAuth: false },
      component: () => import('../components/quotes/QuoteViewId.vue'),
    },
    {
      path: '/quote/preview/kila/:id',
      name: 'quote-preview-kila',
      meta: { requiresAuth: false },
      component: () => import('../components/quotes/kila/QuoteViewIdKila.vue'),
    },

    {
      path: '/trial',
      component: OnboardingLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/trial/start'
        },
        {
          path: 'start',
          name: 'onboarding-start',
          component: () => import('../pages/onboarding/OnboardingStart.vue'),
          meta: { requiresEmailVerified: true }
        },
        {
          path: 'company',
          name: 'onboarding-company',
          component: () => import('../pages/onboarding/OnboardingCompany.vue'),
        },
        {
          path: 'industry',
          name: 'onboarding-industry',
          component: () => import('../pages/onboarding/OnboardingIndustry.vue'),
        },
        {
          path: 'operation-type',
          name: 'onboarding-operation-type',
          component: () => import('../pages/onboarding/OnboardingOperationType.vue'),
        },
        {
          path: 'volume',
          name: 'onboarding-volume',
          component: () => import('../pages/onboarding/OnboardingVolume.vue'),
        },
        {
          path: 'references',
          name: 'onboarding-references',
          component: () => import('../pages/onboarding/OnboardingReferences.vue'),
        },
        {
          path: 'problems',
          name: 'onboarding-problems',
          component: () => import('../pages/onboarding/OnboardingProblems.vue'),
        },
        {
          path: 'source',
          name: 'onboarding-source',
          component: () => import('../pages/onboarding/OnboardingSource.vue'),
        },
        {
          path: 'complete',
          name: 'onboarding-complete',
          component: () => import('../pages/onboarding/OnboardingComplete.vue'),
        },
      ],
    },
    {
      path: '/dashboard',
      component: DashboardLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('../pages/dashboard/DashboardPage.vue'),
        },
        // Analytics Routes
        {
          path: 'analytics',
          name: 'analytics',
          component: () => import('../pages/dashboard/AnalyticsPage.vue'),
        },
        {
          path: 'ai-agents',
          name: 'ai-agents',
          component: () => import('../pages/dashboard/AIAgentsPage.vue'),
        },
        // Operations Routes
        {
          path: 'shipments',
          name: 'shipments',
          component: () => import('../pages/dashboard/ShipmentsPage.vue'),
        },
        {
          path: 'shipment-requests',
          name: 'shipment-requests',
          component: () => import('../pages/dashboard/ShipmentRequestsPage.vue'),
          beforeEnter: (_to, _from, next) => {
            const companyStore = useCompanyStore()
            companyStore.showShipmentRequest ? next() : next({ name: 'shipments' })
          },
        },
        {
          path: 'shipment-requests/new',
          name: 'shipment-request-new',
          component: () => import('../pages/dashboard/ShipmentRequestNewPage.vue'),
          beforeEnter: (_to, _from, next) => {
            const companyStore = useCompanyStore()
            companyStore.showShipmentRequest ? next() : next({ name: 'shipments' })
          },
        },
        {
          path: 'shipment-requests/:id',
          name: 'shipment-request-detail',
          component: () => import('../pages/dashboard/ShipmentRequestDetailPage.vue'),
          beforeEnter: (_to, _from, next) => {
            const companyStore = useCompanyStore()
            companyStore.showShipmentRequest ? next() : next({ name: 'shipments' })
          },
        },
        {
          path: 'shipment-requests/:id/edit',
          name: 'shipment-request-edit',
          component: () => import('../pages/dashboard/ShipmentRequestNewPage.vue'),
          beforeEnter: (_to, _from, next) => {
            const companyStore = useCompanyStore()
            companyStore.showShipmentRequest ? next() : next({ name: 'shipments' })
          },
        },
        {
          path: 'document-management',
          name: 'document-management',
          component: () => import('../pages/dashboard/DocumentManagementPage.vue'),
        },
        {
          path: 'quotes',
          name: 'quotes',
          component: () => import('../pages/dashboard/QuotesPage.vue'),
        },
        {
          path: 'quotes/:id',
          name: 'quote',
          component: () => import('../components/quotes/[id]/QuoteId.vue'),
        },
        {
          path: 'quotes/new',
          name: 'new-quote',
          component: () => import('../components/quotes/NewQuote.vue'),
        },
        {
          path: 'purchase-orders',
          name: 'purchase-orders',
          component: () => import('../pages/purchase-orders/index.vue'),
        },
        {
          path: 'purchase-orders/new',
          name: 'purchase-orders-new',
          component: () => import('../pages/purchase-orders/new/index.vue'),
        },
        {
          path: 'purchase-orders/:id',
          name: 'purchase-order-details',
          component: () => import('../pages/purchase-orders/detail.vue'),
        },
        {
          path: 'invoice-conciliation',
          name: 'invoice-conciliation',
          component: () => import('../pages/invoice-conciliation/index.vue'),
        },
        {
          path: 'purchase-orders/invoice-extraction',
          name: 'purchase-orders-invoice-extraction',
          component: () => import('../pages/purchase-orders/invoice-extraction/index.vue'),
        },
        {
          path: 'inventory',
          name: 'inventory',
          component: () => import('../pages/dashboard/InventoryPage.vue'),
        },
        {
          path: 'warehouses',
          name: 'warehouses',
          component: () => import('../pages/dashboard/WarehousesPage.vue'),
        },
        {
          path: 'warehouses/:id',
          name: 'warehouse-details',
          component: () =>
            import('../pages/dashboard/WarehouseDetailsPage.vue'),
        },
        {
          path: 'warehouse-receipts',
          name: 'warehouse-receipts',
          component: () =>
            import('../pages/dashboard/WarehouseReceiptsPage.vue'),
        },
        {
          path: 'warehouse-receipts/:id',
          name: 'warehouse-receipt-detail',
          component: () =>
            import('../pages/dashboard/WarehouseReceiptDetailPage.vue'),
        },
        {
          path: 'routes',
          name: 'routes',
          component: () => import('../pages/dashboard/RoutesPage.vue'),
        },
        {
          path: 'pickup-request',
          name: 'pickup-request',
          component: () => import('../pages/dashboard/PickupRequestPage.vue'),
        },
        // Management Routes
        {
          path: 'reports',
          name: 'reports-create',
          component: () => import('../pages/dashboard/reports/index.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'reports/my-reports',
          name: 'reports-my-reports',
          component: () => import('../pages/dashboard/reports/my-reports.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'reports/history',
          name: 'reports-history',
          component: () => import('../pages/dashboard/reports/history.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'reports/:id',
          name: 'reports-edit',
          component: () => import('../pages/dashboard/reports/[id].vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: () => import('../pages/dashboard/CalendarPage.vue'),
        },
        {
          path: 'tasks',
          name: 'tasks',
          component: () => import('../pages/dashboard/TasksPage.vue'),
        },
        // Collaboration Routes
        {
          path: 'messages',
          name: 'messages',
          component: () => import('../pages/dashboard/MessagesPage.vue'),
        },
        {
          path: 'team',
          name: 'team',
          component: () => import('../pages/dashboard/TeamPage.vue'),
        },
        // Alerts Module
        {
          path: 'alerts',
          name: 'alerts',
          component: () => import('../pages/dashboard/AlertsPage.vue'),
        },
        // Settings Routes
        {
          path: 'settings',
          name: 'settings',
          component: () => import('../pages/dashboard/SettingsPage.vue'),
        },
        {
          path: 'settings/data-import',
          name: 'data-import-settings',
          component: () => import('../pages/settings/DataImportSettings.vue'),
          meta: {
            requiresAuth: true,
            layout: 'dashboard'
          }
        },
        {
          path: 'settings/fields',
          name: 'fields-management',
          component: () => import('../components/fields-management/FieldsManagementDemo.vue'),
          meta: {
            requiresAuth: true,
            layout: 'dashboard'
          }
        },
        {
          path: 'billing',
          name: 'billing',
          component: () => import('@/pages/dashboard/BillingPage.vue'),
          meta: {
            requiresAuth: true,
            layout: 'dashboard'
          }
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('../pages/dashboard/ProfilePage.vue'),
        },
        // Tracking Routes
        {
          path: 'tracking-request',
          name: 'tracking-request',
          component: () => import('../pages/dashboard/TrackingRequestPage.vue'),
        },
        {
          path: 'tracking-results',
          name: 'tracking-results',
          component: () => import('../pages/dashboard/TrackingResultsPage.vue'),
        },
        // Email Management Routes
        {
          path: 'emails',
          component: () => import('../pages/email/HomeView.vue'),
          children: [
            {
              path: '',
              name: 'emails',
              component: () => import('../pages/email/InboxView.vue'),
            },
            {
              path: 'inbox',
              name: 'email-inbox',
              component: () => import('../pages/email/InboxView.vue'),
            },
            {
              path: 'starred',
              name: 'email-starred',
              component: () => import('../pages/email/StarredView.vue'),
            },
            {
              path: 'sent',
              name: 'email-sent',
              component: () => import('../pages/email/SentView.vue'),
            },
            {
              path: 'spam',
              name: 'email-spam',
              component: () => import('../pages/email/SpamView.vue'),
            },
            {
              path: 'archived',
              name: 'email-archived',
              component: () => import('../pages/email/ArchivedView.vue'),
            },
            {
              path: 'trash',
              name: 'email-trash',
              component: () => import('../pages/email/TrashView.vue'),
            },
            {
              path: 'drafts',
              name: 'email-drafts',
              component: () => import('../pages/email/DraftsView.vue'),
            },
            {
              path: 'folder/:folderId',
              name: 'email-folder',
              props: true,
              component: () => import('../pages/email/FolderView.vue'),
            },
            {
              path: 'detail/:id',
              name: 'email-detail',
              props: true,
              component: () => import('../pages/email/EmailDetailView.vue'),
              beforeEnter: (to, from, next) => {
                const DEBUG_SKIP_EMAIL_AUTH = false;
                const emailAuth = useEmailAuthStore();
                if (!DEBUG_SKIP_EMAIL_AUTH && !emailAuth.isAuthenticated) {
                  next({ path: '/dashboard/emails' });
                } else {
                  next();
                }
              },
            },
          ],
        },
        {
          path: 'emails/login',
          name: 'email-login',
          component: () => import('../pages/email/LoginView.vue'),
        },
        {
          path: 'emails/callback',
          name: 'email-callback',
          component: () => import('../pages/email/CallbackView.vue'),
        },
        // Usage Analytics Routes
        {
          path: 'usage-activity',
          name: 'usage-activity',
          component: () => import('../pages/dashboard/UsageActivityPage.vue'),
        },
        {
          path: 'usage-activity/:id',
          name: 'processing-detail',
          component: () => import('../pages/dashboard/ProcessingDetailPage.vue'),
          props: true,
        },
        {
          path: 'data-processing-usage',
          name: 'data-processing-usage',
          component: () => import('../pages/dashboard/DataProcessingUsagePage.vue'),
        },
        {
          path: 'user-performance',
          name: 'user-performance',
          component: () => import('../pages/dashboard/UserPerformancePage.vue'),
        },
        // Health Monitoring Route
        {
          path: 'health',
          name: 'health',
          component: () =>
            import('../pages/dashboard/HealthMonitoringPage.vue'),
        },
        // Support
        {
          path: 'support',
          children: [
            {
              path: '',
              name: 'support',
              component: () => import('../pages/support/SupportLandingPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'tickets',
              name: 'support-tickets',
              component: () => import('../pages/support/SupportListPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'tickets/new',
              name: 'support-tickets-new',
              component: () => import('../pages/support/CreateSupportTicketPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'tickets/:id',
              name: 'support-tickets-detail',
              component: () => import('../pages/support/SupportTicketDetailPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'help',
              name: 'support-help',
              component: () => import('../pages/support/SupportHelpPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'help/questions',
              name: 'support-help-questions',
              component: () =>
                import('../pages/support/SupportHelpQuestionsPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              path: 'help/tours',
              name: 'support-help-tours',
              component: () => import('../pages/support/SupportHelpToursPage.vue'),
              meta: { requiresAuth: true }
            },
            {
              // Backwards-compatible alias:
              // /dashboard/support/new -> /dashboard/support/tickets/new
              path: 'new',
              redirect: '/dashboard/support/tickets/new'
            },
            {
              // Backwards-compatible alias:
              // /dashboard/support/:id -> /dashboard/support/tickets/:id
              path: ':id',
              redirect: (to) => `/dashboard/support/tickets/${to.params.id}`
            }
          ]
        },
        // DocGen AI Module
        ...docgenRoutes,
        // Kila AI Assistant Module
        ...assistantRoutes,
      ],
    },
    // Dashboard IA a pantalla completa (sin layout)
    ...assistantStandaloneRoutes,
    // Support aliases (requested /support/* paths)
    {
      path: '/support',
      redirect: '/dashboard/support',
      meta: { requiresAuth: true }
    },
    {
      path: '/support/tickets',
      redirect: '/dashboard/support/tickets',
      meta: { requiresAuth: true }
    },
    {
      path: '/support/tickets/new',
      redirect: '/dashboard/support/tickets/new',
      meta: { requiresAuth: true }
    },
    {
      path: '/support/tickets/:id',
      redirect: (to) => `/dashboard/support/tickets/${to.params.id}`,
      meta: { requiresAuth: true }
    },
    {
      path: '/support/help',
      redirect: '/dashboard/support/help',
      meta: { requiresAuth: true }
    },
    {
      path: '/support/help/questions',
      redirect: '/dashboard/support/help/questions',
      meta: { requiresAuth: true }
    },
    {
      path: '/support/help/tours',
      redirect: '/dashboard/support/help/tours',
      meta: { requiresAuth: true }
    },
    {
      path: '/public',
      component: PublicLayout,
      children: [
        {
          path: 'shipments/:shareId',
          name: 'PublicShipmentDetail',
          component: () => import('../pages/public/PublicShipmentDetail.vue')
        }
      ]
    },
    {
      path: '/quote/view/:id',
      name: 'quote-view',
      meta: { requiresAuth: false },
      component: () => import('../pages/public/QuoteClientId.vue'),
    },
    {
      path: '/quote/view/kila/:id',
      name: 'quote-view-kila',
      meta: { requiresAuth: false },
      component: () => import('../pages/public/kila/QuoteClientIdKila.vue'),
    },
    {
      path: '/quote/link/:id',
      name: 'quote-link',
      meta: { requiresAuth: false },
      component: () => import('../pages/public/QuoteEmailViewId.vue'),
    },
    {
      path: '/quote/link/kila/:id',
      name: 'quote-link-kila',
      meta: { requiresAuth: false },
      component: () => import('../pages/public/kila/QuoteEmailViewIdKila.vue'),
    },
    // Catch-all route for 404
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../pages/NotFoundPage.vue'),
    },
  ],
})

/**
 * Ruta a la que mandar al usuario si intenta entrar a un módulo que no tiene, o
 * null si puede pasar.
 *
 * Los permisos dependen de los flags de la compañía, así que primero se esperan
 * sus datos: decidir con información incompleta sacaría al usuario de un módulo
 * que sí tiene, y dejar pasar sin decidir abría por URL directa un módulo que no.
 * Si la compañía no se puede cargar no se bloquea nada.
 */
async function resolveBlockedModuleRedirect(path: string): Promise<string | null> {
  const companyStore = useCompanyStore()
  await companyStore.ensureCompanyDataLoaded()
  if (!companyStore.currentCompanyData) return null

  const modules = modulesForPath(path)
  if (modules.length === 0) return null

  const { canRead } = usePermissions()
  // Basta con poder leer cualquiera de los módulos de la ruta.
  if (modules.some((module) => canRead(module))) return null

  const fallback = navigationItems.find((item) => canRead(item.module))?.to
  return fallback || '/dashboard/profile'
}

/**
 * Reevalúa la ruta actual con los permisos vigentes y la reemplaza si quedó
 * bloqueada. El guard solo corre al navegar; cambiar de compañía cambia el rol
 * sin navegar, así que el selector de compañía llama a esto.
 */
export async function reevaluateCurrentRoute(): Promise<void> {
  const current = router.currentRoute.value.path
  const redirect = await resolveBlockedModuleRedirect(current)
  if (redirect && redirect !== current) await router.replace(redirect)
}

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const loadingStore = useLoadingStore()


  loadingStore.startLoading()

  try {
    // Siempre verificar el estado de autenticación
    const isAuthenticated = await authStore.checkAuth()

    if (to.meta.requiresAuth && !isAuthenticated) {
      // Tres estados, no dos: si Firebase tiene sesión viva y lo único que
      // falta es el perfil, mandar al login expulsa a alguien que sí está
      // autenticado. Se deja pasar y la siguiente navegación reintenta.
      if (authStore.hasFirebaseSession) {
        next()
        return
      }
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }

    // Si la ruta requiere verificación de correo

/*     // Obtener las rutas permitidas según el plan del usuario
    const planName = getPlanName()
    const allowedDashboardRoutes = planModules[planName]?.routes || [] as string[]
    const isRouteAllowed = allowedDashboardRoutes.some((route:string) => route === to.path) */

    // Si el usuario está autenticado pero intenta acceder a rutas públicas o no permitidas
    if (authStore.user) {
      if (to.path === '/login' || to.path === '/signup') {
        const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : null
        next(redirect || '/dashboard/tracking-request')
        return
      }

      // Redirect support admins directly to ticket management
      if (to.name === 'support' && authStore.isSupportAdmin) {
        next('/dashboard/support/tickets')
        return
      }

      // La configuración de solo vista decide a qué módulos entra un viewer, así
      // que tiene que estar cargada antes de resolver el bloqueo. Es idempotente
      // y no hace nada para el resto de roles.
      await ensureViewerConfigLoaded()
      // El rol personalizado decide a qué módulos entra; tiene que estar cargado antes de resolver el bloqueo.
      await ensureSessionCustomRoleLoaded()

      const blockedRedirect = await resolveBlockedModuleRedirect(to.path)
      if (blockedRedirect) {
        next(blockedRedirect)
        return
      }
/*       
      if (!isRouteAllowed) {
        next('/dashboard/tracking-request')
        return
      } */
    }

    next()
  } catch (error) {
    console.error('Error en la navegación:', error)
    // Si hay un error de autenticación, redirigir al login
    if (to.meta.requiresAuth) {
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }
    next()
  } finally {
    loadingStore.stopLoading()
  }
})

// Recuperación ante módulos dinámicos que no cargan. Ocurre cuando Vite
// re-optimiza dependencias en dev, o cuando un deploy nuevo invalida los chunks
// que una pestaña abierta aún referencia: el import() de la ruta falla con
// "Failed to fetch dynamically imported module". Recargar una vez hacia el
// destino resuelve ambos casos.
router.onError((error, to) => {
  const message = (error as Error)?.message || ''
  const isDynamicImportError =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      message
    )
  if (!isDynamicImportError) return

  // Guardia anti-bucle: un solo reintento por destino. Se limpia en afterEach
  // cuando la navegación sí llega, para permitir futuros reintentos.
  const reloadKey = `router:reload:${to.fullPath}`
  if (sessionStorage.getItem(reloadKey)) return
  sessionStorage.setItem(reloadKey, '1')
  window.location.assign(to.fullPath)
})

router.afterEach((to) => {
  sessionStorage.removeItem(`router:reload:${to.fullPath}`)
})

export default router
